/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import { CurrencyType } from '../Localization/Currency';
import { AutoPlayManager } from '../Playbar/AutoPlayManager';
import BetManager from '../Playbar/BetManager';
import Communicator from '../Server/Communicator';
import IErrorParams from '../Server/Error/IErrorParams';
import IBalanceUpdateData from '../Server/Object/IBalanceUpdateData';
import PlayResultComponent from '../Server/PlayResultComponent';
import { RGSErrorCode } from '../Server/RGSErrorCode';
import StateComponent from '../StateManager/StateComponent';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateReference from '../StateManager/StateReference';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import { ContextKey } from '../Utility/Component/Configurator/ContextKey';
import IPopupManager from '../Utility/Component/Popup/IPopupManager';
import { IPopupModal, IPopupOptions } from '../Utility/Component/Popup/IPopupModal';
import PopupModal from '../Utility/Component/Popup/PopupModal';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import DtModObservable from './DtModObservable';
import { ClientEvent } from './Integrations/ClientEvent';
import ClientEvents from './Integrations/ClientEvents';
import AutoResizingCanvas from '../Utility/AutoResizingCanvas';
import GoogleAnalytics from '../Utility/GoogleAnalytics';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** The main touch point for core game logic. Manages autoplay, game speed, play state advancement, and game results. */
@cc._decorator.ccclass
export default class Game extends StateDependencyComponent
{
    /** The advance play event string. */
    public static readonly AdvancePlayEventName: string = "Game.AdvancePlay";

    /** Game speed modifier. */
    private _dtModObservable: DtModObservable = null;
    public get DtModObservable(): DtModObservable { return this._dtModObservable; }

    // #region CC Settings

    /** Reference to the game's root node. */
    @property({ type: cc.Node, visible: true })
    private _topNode: cc.Node = null;
    /** Accessor for the game's root node. */
    public get TopNode(): cc.Node { return this._topNode; }

    /** State on which the game should issue the advance play event. */
    @property({ type: StateReference, visible: true })
    private _advancePlayOnState: StateReference = new StateReference();

    /** State on which the game should wait for input after a play. */
    @property({ type: StateReference, visible: true })
    private _pauseForInputState: StateReference = new StateReference();

    /** @hidden */
    private _playResultRef: InjectableComponent<PlayResultComponent> = new InjectableComponent();
    /** Accessor for the game's current play result. */
    public get CurrentPlayResult(): PlayResultComponent { return this._playResultRef.Value; }
    /** Mutator for the game's current play result. */
    public set CurrentPlayResult(value: PlayResultComponent) { this._playResultRef.Value = value; }

    /** @hidden */
    private _autoPlayManagerRef: InjectableComponent<AutoPlayManager> = new InjectableComponent();

    /** @hidden */
    private _autoResizingCanvasRef: InjectableComponent<AutoResizingCanvas> = new InjectableComponent();

    /** @hidden */
    private _betManagerRef: InjectableComponent<BetManager> = new InjectableComponent();

    /** @hidden */
    private _clientEventsRef: InjectableComponent<ClientEvents> = new InjectableComponent();

    /** @hidden */
    @property(InjectableComponent)
    protected popupManagerRef: InjectableComponent<IPopupManager> = new InjectableComponent();

    /** @hidden */
    protected popupModalRef: InjectableComponent<IPopupModal> = new InjectableComponent();

    /** @hidden */
    protected communicatorRef: InjectableComponent<Communicator> = new InjectableComponent();

    /** @hidden */
    private _advancePlayEventDispatcher: cc.EventTarget = new cc.EventTarget();
    /** Accessor for the advance play event target so that handlers can be registered. */
    public get AdvancePlayEventDispatcher(): cc.EventTarget { return this._advancePlayEventDispatcher; }

    // #endregion

    // #region CC Lifecycle

    /** @hidden */
    protected setContexts(): void
    {
        this._betManagerRef.SetContext(BetManager);
        this._clientEventsRef.SetContext(ClientEvents);
        this._playResultRef.SetContext(PlayResultComponent);
        this._autoPlayManagerRef.SetContext(AutoPlayManager);
        this._autoResizingCanvasRef.SetContext(AutoResizingCanvas);
        this.popupManagerRef.SetContext(ContextKey.POPUP_MANAGER);
        this.popupModalRef.SetContext(PopupModal);
        this.communicatorRef.SetContext(Communicator);

        this.configureWindowListener();
    }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this._dtModObservable = this.addComponent(DtModObservable);
        this._dtModObservable.IsActive = true;
    }

    /** @hidden */
    protected start(): void
    {
        super.start(this._advancePlayOnState, this._pauseForInputState);
    }

    /** @hidden */
    protected firstUpdate(): void
    {
        if (!this._advancePlayOnState.Value) { return; }

        this.scheduleOnce(() => this._advancePlayOnState.Value.StateManager.StartPlay(), 0.5);
    }

    // #endregion

    // #region Event Handlers

    /** Public entry point for making the game proceed. */
    public AdvancePlay(): void
    {
        this.advancePlay();
    }

    /** Reacts to spin button clicks by cancelling out of autoplay or advancing the play state. */
    public OnSpinButtonClicked(event: cc.Event.EventCustom): void
    {
        CLog.log(`Spin button clicked.`, LogTypes.Game, this);

        if (this._autoPlayManagerRef.Value.IsAutoPlaying)
        {
            this._autoPlayManagerRef.Value.ClearAutoPlay();
            return;
        }

        this.advancePlay();
    }

    // #endregion

    // #region Private Helpers

    /** Advances play based on current game state. */
    private advancePlay = (): void =>
    {
        if (this._advancePlayOnState.Value.IsCurrentState())
        {
            this.placeBet(this._advancePlayOnState.Value);
        }
        else if (this._pauseForInputState.Value.IsCurrentState())
        {
            this.placeBet(this._pauseForInputState.Value);
        }

        this._advancePlayEventDispatcher.emit(Game.AdvancePlayEventName);
    }

    /** Places bet with the bet manager when initiating a spin. */
    private placeBet(state: StateComponent, displayError: boolean = false): void
    {
        if (!this._betManagerRef.Value) { return; }

        if (this._betManagerRef.Value.PlaceBet())
        {
            GoogleAnalytics.SpinEvent(this._autoResizingCanvasRef.Value.IsLandScape);
            this.completeState(state);
            return;
        }

        // Add additional requirement.
        if (displayError)
        {
            if (!this.popupModalRef.Value) { return; }

            let options: IPopupOptions =
            {
                MessageId: RGSErrorCode[RGSErrorCode.INSUFFICIENT_FUND],
                IncludeOkButton: true,
                IncludeCancelButton: false,
            };

            let params: IErrorParams =
            {
                textCode: "2",
                prompts: []
            };

            let bet: number = this._betManagerRef.Value.CurrentBetCredits;
            let balance: number = this._betManagerRef.Value.GetBalance().GetValue(CurrencyType.CREDITS);
            let eventData: object =
            {
                balance,
                betTarget: bet
            };
            this.communicatorRef.Value.ReportClientEvent(ClientEvent.INSUFFICIENT_FUND, eventData);
            this._clientEventsRef.Value.ReportError(ClientEvent[ClientEvent.INSUFFICIENT_FUND], params, options);
        }
        else
        {
            this.communicatorRef.Value.RequestBalance((result: IBalanceUpdateData) =>
            {
                if (!result) { return; }

                if (result.balanceDetails && this.CurrentPlayResult.PlayState)
                {
                    this.CurrentPlayResult.PlayState.balanceDetails = result.balanceDetails;
                }

                if (result.platformNotification)
                {
                    this._clientEventsRef.Value.WalletNotification(result.platformNotification);
                }

                this._betManagerRef.Value.SetBalance(result.balanceMc);
                this.placeBet(state, true);
            });
        }
    }

    private configureWindowListener(): void
    {
        if (cc.sys.isBrowser && window && window.addEventListener)
        {
            window.addEventListener('message', (event: MessageEvent) =>
            {
                const result: any = event.data;
                if (result.action === 'everi.action.spin')
                {
                    if (result.dtMod) { this._dtModObservable.Value = result.dtMod; }

                    this.communicatorRef.Value.ReportClientEvent(ClientEvent.ADVANCE_PLAY);
                    this.AdvancePlay();
                }
            });
        }
    }

    // #endregion
}
